import React, { useEffect, useMemo, useState } from 'react'
import { Button, CircularProgress, IconButton, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled'
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled'
import WarningIcon from '@material-ui/icons/Warning'
import { useOfficeTimeTracker } from '../context/OfficeTimeTrackerContext'
import { formatSeconds, getElapsedTodaySeconds, normalizeRole, TRACKER_ROLES } from '../utils/timeTrackerUi'

interface Props {
  onClick: () => void
}

const useStyles = makeStyles({
  tooltipPopper: {
    '&[x-placement*="bottom"] $tooltip': {
      transformOrigin: 'top center'
    }
  },
  tooltip: {
    background: '#fff2e8',
    color: '#9a3412',
    fontSize: '0.98rem',
    fontWeight: 700,
    lineHeight: 1.4,
    padding: '14px 16px 14px 18px',
    borderRadius: 18,
    boxShadow: '0 18px 42px rgba(194, 65, 12, 0.28)',
    border: '2px solid rgba(255, 105, 0, 0.18)',
    animation: '$tooltipBounce 0.9s ease-in-out 1'
  },
  arrow: {
    color: '#fff2e8'
  },
  tooltipContent: {
    position: 'relative',
    minWidth: 240,
    paddingRight: 24
  },
  closeButton: {
    position: 'absolute',
    top: -10,
    right: -14,
    color: '#c2410c',
    padding: 4
  },
  buttonWrap: {
    display: 'inline-flex',
    animation: '$buttonPulse 1.15s ease-in-out 1'
  },
  navbarButton: {
    color: '#fff7ed',
    background: 'rgba(255, 220, 187, 0.36)',
    border: '1px solid rgba(255, 245, 235, 0.14)',
    textTransform: 'none',
    minWidth: 164,
    minHeight: 42,
    whiteSpace: 'nowrap',
    borderRadius: 999,
    padding: '8px 16px',
    fontWeight: 800,
    letterSpacing: '0.02em',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
    '&:hover': {
      background: 'rgba(255, 229, 204, 0.48)'
    },
    '&.Mui-disabled': {
      color: '#fff7ed',
      background: 'rgba(255, 220, 187, 0.26)'
    }
  },
  navbarButtonContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minWidth: 0
  },
  alertButton: {
    maxWidth: 'clamp(220px, 32vw, 320px)',
    minWidth: 0,
    justifyContent: 'flex-start',
    whiteSpace: 'normal',
    textAlign: 'left',
    padding: '8px 14px'
  },
  statusIcon: {
    flexShrink: 0,
    fontSize: 18
  },
  alertMessage: {
    display: '-webkit-box',
    overflow: 'hidden',
    lineHeight: 1.2,
    wordBreak: 'break-word',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2
  },
  '@keyframes tooltipBounce': {
    '0%': { transform: 'translateY(0) scale(1)' },
    '25%': { transform: 'translateY(-7px) scale(1.02)' },
    '55%': { transform: 'translateY(3px) scale(0.99)' },
    '75%': { transform: 'translateY(-3px) scale(1.01)' },
    '100%': { transform: 'translateY(0) scale(1)' }
  },
  '@keyframes buttonPulse': {
    '0%': { transform: 'scale(1)', boxShadow: '0 0 0 rgba(255, 105, 0, 0)' },
    '50%': {
      transform: 'scale(1.04)',
      boxShadow: '0 0 0 10px rgba(255, 105, 0, 0.14)'
    },
    '100%': { transform: 'scale(1)', boxShadow: '0 0 0 rgba(255, 105, 0, 0)' }
  }
})

const TimeTrackerNavbarControl: React.FC<Props> = ({ onClick }) => {
  const classes = useStyles()
  const { featureEnabled, initialized, loading, normalizedRole, session } = useOfficeTimeTracker()
  const [dismissedTooltip, setDismissedTooltip] = useState(false)
  const [ticker, setTicker] = useState(0)
  const normalizedTrackerRole = useMemo(() => normalizeRole(normalizedRole) || '', [normalizedRole])
  const bootstrapLoading = loading && !initialized

  useEffect(() => {
    if (!bootstrapLoading) {
      setDismissedTooltip(false)
    }
  }, [bootstrapLoading])

  useEffect(() => {
    if (session?.status !== 'running') {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      setTicker(previousTicker => previousTicker + 1)
    }, 1000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [session?.runningSince, session?.status])

  if ((!featureEnabled && !bootstrapLoading) || !normalizedRole || !TRACKER_ROLES.has(normalizedTrackerRole)) {
    return null
  }

  const elapsedToday = getElapsedTodaySeconds(session)
  void ticker
  const isFinishedDayState =
    session?.dayClosed &&
    session?.navbarMode === 'alert' &&
    session?.status !== 'running' &&
    session?.status !== 'paused'

  const showTooltip =
    !dismissedTooltip &&
    session?.navbarMode === 'ingreso' &&
    !session?.dayClosed &&
    !session?.runningSince &&
    !session?.startedAt

  const button = (
    <Button
      className={`${classes.navbarButton} ${
        session?.navbarMode === 'alert' || isFinishedDayState ? classes.alertButton : ''
      }`}
      onClick={onClick}
      disabled={bootstrapLoading}
    >
      <span className={classes.navbarButtonContent}>
        {bootstrapLoading ? (
          <CircularProgress size={20} thickness={5} style={{ color: '#fff7ed' }} />
        ) : isFinishedDayState ? (
          <>
            <CheckCircleIcon style={{ marginRight: 2, fontSize: 18 }} />
            Jornada finalizada
          </>
        ) : session?.navbarMode === 'alert' ? (
          <>
            <WarningIcon className={classes.statusIcon} style={{ marginRight: 2 }} />
            <span className={classes.alertMessage}>{session?.navbarMessage || 'Revisa tu jornada'}</span>
          </>
        ) : session?.navbarMode === 'timer_paused' ? (
          <>
            <PauseCircleFilledIcon style={{ marginRight: 2, fontSize: 18 }} />
            {formatSeconds(elapsedToday)}
          </>
        ) : session?.navbarMode === 'timer_running' ? (
          <>
            <PlayCircleFilledIcon style={{ marginRight: 2, fontSize: 18 }} />
            {formatSeconds(elapsedToday)}
          </>
        ) : (
          <>
            <PlayCircleFilledIcon style={{ marginRight: 2, fontSize: 18 }} />
            Ingreso
          </>
        )}
      </span>
    </Button>
  )

  if (!showTooltip) {
    return button
  }

  return (
    <Tooltip
      title={
        <div className={classes.tooltipContent}>
          <IconButton className={classes.closeButton} size="small" onClick={() => setDismissedTooltip(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
          Recuerda marcar tu ingreso
        </div>
      }
      open
      arrow
      interactive
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.arrow,
        popper: classes.tooltipPopper
      }}
    >
      <span className={classes.buttonWrap}>{button}</span>
    </Tooltip>
  )
}

export default TimeTrackerNavbarControl
